import { FatturaRicevutaDto, ProjectExternalCost, SearchFattureRicevuteRequest } from '@api/client';
import { useEffect, useMemo, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import NoData from 'shared/design-system/components/no-data-box';
import { formatCurrency } from 'shared/utils/common.utils';
import AssociateCostsToPassiveInvoiceModal from '../associate-costs-to-passive-invoice-modal';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { disassociateInvoicePassiveCycle } from 'pages/private/active-cycle/active-cycle-order-list/_redux/actions';
import { AppToastService } from 'shared/design-system/components/app-toast/app-toast.service';
import ModalAction from 'pages/private/project-list/project-detail/_components/modal-action';
import {
  getFattureRicevute,
  getFattureRicevuteExternalCollaborators,
} from 'pages/private/active-cycle/active-cycle-bills/_redux/actions';
import AssociateExternalCollaboratorsToPassiveInvoiceModal from '../associate-external-collaborators-to-passive-cycle-invoice-modal';
import ServerSidePaginator from 'shared/design-system/components/app-server-side-paginator';
import {
  fattureRicevuteAssociablePagingSelector,
  filtersPassiveAssociableInvoicesUsedSelector,
} from 'pages/private/active-cycle/active-cycle-bills/_redux/selectors';

interface Props {
  invoices: FatturaRicevutaDto[];
  associatedProjects?: ProjectExternalCost[];
  idOrdine?: number | undefined;
  readOnly: boolean;
  externalCollaboratorsAssociation: boolean;
  editCollaborators: boolean;
}

type FatturaRicevutaDtoWithProject = FatturaRicevutaDto & { projectId: number | undefined };

const ActionColumnMapping: Record<string, JSX.Element> = {
  Add: <i className="fas fa-plus-circle text-blue-links" />,
  Costs: <i className="icon-eye-shows text-blue-links" />,
  Collab: <i className="icon-edit text-blue-links" />,
};

const PassiveOrderAssociatedInvoicesTable: React.FC<Props> = ({
  invoices,
  associatedProjects,
  idOrdine,
  readOnly,
  externalCollaboratorsAssociation,
  editCollaborators,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const filterUsed = useAppSelector(filtersPassiveAssociableInvoicesUsedSelector);
  const fattureRicevuteAssociablePaging = useAppSelector(fattureRicevuteAssociablePagingSelector);
  const [invoicesList, setInvoicesList] = useState<FatturaRicevutaDtoWithProject[]>([]);
  const [isAssociateCostToInvoiceModalVisible, setIsAssociateCostToInvoiceModalVisible] = useState(false);
  const [isAssociateExternalCollabModalVisible, setIsAssociateExternalCollabModalVisible] = useState(false);
  const [currentInvoiceId, setCurrentInvoiceId] = useState<string | undefined>(undefined);
  const [dissociateInvoiceModalVisible, setDissociateInvoiceModalVisible] = useState<boolean>(false);
  const [invoiceToDeleteId, setInvoiceToDeleteId] = useState<(number | undefined)[]>([]);

  useEffect(() => {
    if (invoices) {
      setInvoicesList(invoices.map((invoice) => ({ ...invoice, projectId: undefined })));
    }
  }, [invoices]);

  const actionColumnHeader = useMemo(() => {
    if (editCollaborators) {
      return 'Collab';
    } else if (readOnly && !externalCollaboratorsAssociation) {
      return 'Costs';
    } else {
      return 'Add';
    }
  }, [readOnly, editCollaborators, externalCollaboratorsAssociation]);

  const actionColumnIcon = useMemo(() => {
    return ActionColumnMapping[actionColumnHeader] || null;
  }, [actionColumnHeader]);

  const handleDissociateInvoice = async () => {
    if (!invoiceToDeleteId.length) return;

    try {
      await Promise.all(
        invoiceToDeleteId.filter(Boolean).map((id) => dispatch(disassociateInvoicePassiveCycle(id as number)))
      );

      AppToastService.success(t('Invoice dissociated successfully'));
      setDissociateInvoiceModalVisible(false);

      if (!idOrdine) return;
      if (externalCollaboratorsAssociation) {
        dispatch(getFattureRicevuteExternalCollaborators(idOrdine));
      } else {
        dispatch(
          getFattureRicevute({
            request: { fatturaRicevutaSearchDto: { numeroOrdine: String(idOrdine), paging: { size: 9999, page: 0 } } },
            type: 'associated',
          })
        );
      }
    } catch (error) {
      console.error('Errore durante la dissociazione delle fatture:', error);
    }
  };

  const changePage = (page: number) => {
    let filters = { ...filterUsed };
    if (filters.paging) {
      filters.paging = { ...filters.paging, page: page };
    }
    const payload: SearchFattureRicevuteRequest = {
      fatturaRicevutaSearchDto: filters!,
    };
    dispatch(getFattureRicevute({ request: payload, type: 'associable' }));
  };

  if (invoicesList.length) {
    return (
      <div className="table-responsive table-bordered my-3">
        <Table className={'mt-0 mb-0'}>
          <thead className="bg-red">
            <tr>
              <th scope="col">
                <span>{'N.Invoice'}</span>
              </th>
              <th scope="col">
                <span>{'Amount (IVA incl.)'}</span>
              </th>
              <th scope="col">
                <span>{'Amount'}</span>
              </th>
              <th scope="col">
                <span>{'N. document'}</span>
              </th>
              <th scope="col">
                <span>{'Invoice date'}</span>
              </th>
              <th scope="col">
                <span>{'Typology'}</span>
              </th>
              <th scope="col">
                <span>{'Supplier'}</span>
              </th>
              <th scope="col" style={{ width: '300px' }}>
                <span>{'Description'}</span>
              </th>
              <th
                className="text-center"
                scope="col"
                style={{ maxWidth: actionColumnHeader === 'Collab' ? '65px' : '65px' }}
              >
                <span>{actionColumnHeader}</span>
              </th>
              {readOnly && !editCollaborators && (
                <th className="text-center" scope="col" style={{ maxWidth: '65px' }}>
                  <span>{'Delete'}</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {invoicesList.map((invoice) => (
              <tr key={`invoice-${invoice.fkFatturaRicevuta}`}>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{invoice.fkFatturaRicevuta}</p>
                  </span>
                </td>
                <td className="text-end">
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{formatCurrency(invoice.frImportoTotale)}€</p>
                  </span>
                </td>
                <td className="text-end">
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{formatCurrency(invoice.frImponibile)}€</p>
                  </span>
                </td>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{invoice.frNumDocumento}</p>
                  </span>
                </td>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{invoice.dataFattura?.toLocaleDateString('it-IT') || '-'}</p>
                  </span>
                </td>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{invoice.frTipologia}</p>
                  </span>
                </td>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{invoice.denominazioneFornitore}</p>
                  </span>
                </td>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto" title={invoice.frDescrizioneCausale || '-'}>
                      {invoice.frDescrizioneCausale
                        ? `${invoice.frDescrizioneCausale.slice(0, 100)}${
                            invoice.frDescrizioneCausale.length > 100 ? '…' : ''
                          }`
                        : '-'}
                    </p>
                  </span>
                </td>

                <td className="text-center">
                  <Button
                    className="btn text-blue-links border-none p-0"
                    type="button"
                    variant="icon"
                    onClick={() => {
                      setCurrentInvoiceId(invoice.fkFatturaRicevuta);
                      if (externalCollaboratorsAssociation) {
                        setIsAssociateExternalCollabModalVisible(true);
                      } else {
                        setIsAssociateCostToInvoiceModalVisible(true);
                      }
                    }}
                  >
                    {actionColumnIcon}
                  </Button>
                </td>
                {readOnly && !editCollaborators && (
                  <td className="text-center">
                    <Button
                      className="btn text-blue-links border-none p-0"
                      type="button"
                      variant="icon"
                      onClick={() => {
                        setDissociateInvoiceModalVisible(true);
                        setInvoiceToDeleteId(
                          invoice.associatedOrders?.[0].associatedProjects?.map((p) => p.associationId) || []
                        );
                      }}
                    >
                      <i className="fas fa-xmark text-danger" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
        {!editCollaborators && !readOnly && (
          <div className="my-3 px-3">
            <ServerSidePaginator
              currentPage={fattureRicevuteAssociablePaging.page}
              totalPages={fattureRicevuteAssociablePaging.totalPages}
              elementsToShow={50}
              totalElements={fattureRicevuteAssociablePaging.totalElements}
              onPageChange={(e) => changePage(e)}
            />
          </div>
        )}
        <AssociateCostsToPassiveInvoiceModal
          isOpen={isAssociateCostToInvoiceModalVisible}
          onClose={() => setIsAssociateCostToInvoiceModalVisible(false)}
          associatedProjects={associatedProjects}
          idOrdine={idOrdine}
          currentInvoiceId={currentInvoiceId}
          readOnly={readOnly}
        />
        <AssociateExternalCollaboratorsToPassiveInvoiceModal
          isOpen={isAssociateExternalCollabModalVisible}
          onClose={() => setIsAssociateExternalCollabModalVisible(false)}
          idOrdine={idOrdine}
          currentInvoiceId={currentInvoiceId}
          editCollaborators={editCollaborators}
        />
        <ModalAction
          title={t('Dissociate invoice')}
          actionLabel={t('common.confirm')}
          cancelLabel={t('common.cancel')}
          action={() => {
            handleDissociateInvoice();
          }}
          setShow={(value) => setDissociateInvoiceModalVisible(value)}
          show={dissociateInvoiceModalVisible}
        >
          <span className="text-danger">
            <i className="fa-solid fa-triangle-exclamation" /> <b>{t('common.attention')}</b>{' '}
          </span>
          <div className="p-1">
            <p>{t('Are you sure you want to dissociate the invoice?')}</p>
          </div>
        </ModalAction>
      </div>
    );
  } else {
    return (
      <div className="d-flex justify-content-center mt-3">
        <NoData msg={t('No invoices found')} />
      </div>
    );
  }
};

export default PassiveOrderAssociatedInvoicesTable;
